import React, { useState, useEffect } from "react";
import { ChallengeCreate } from "../services/challengeService";
import { stockService, StockUniverse } from "../services/stockService";
import { FiHelpCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { isStockTradingMode } from "../config";
import { CryptoUniverse, cryptoService } from "../services/cryptoService";

interface ChallengeFormProps {
  challenge?: any | null;
  onSubmit: (challengeData: any, publish?: boolean) => void;
  onCancel: () => void;
  loading?: boolean;
}

export default function ChallengeForm({
  challenge,
  onSubmit,
  onCancel,
  loading = false,
}: ChallengeFormProps) {
  const [formData, setFormData] = useState<ChallengeCreate>({
    name: "",
    description: "",
    type: "Weekly",
    entry_fee: 0,
    prize_pool: 0,
    max_participants: 100,
    min_participants: 2,
    gameplay_type: "DYNAMIC",
    allow_overflow_entries: false,
    start_date: "",
    end_date: "",
    stock_universe_ids: [],
    trading_hours_settings: {
      enabled: isStockTradingMode ? true : false,
      fixed_window_start_time: null,
      fixed_window_end_time: null,
      cancel_unexecuted_fixed_limits: true,
      restrict_to_market_hours: true,
    },
    virtual_capital: 1000000,
    brokerage_fee: 0.05,
    challenge_type: "Flexible",
    guaranteed_prize_pool: 0,
    experience_level: "",
    risk_appetite: "",
    allowed_order_types: {
      market_order: true,
      limit_orders: false,
      stop_loss_orders: false,
    },
    trading_restrictions: {
      allow_short_selling: false,
      intraday_only: false,
      max_open_positions: 10,
      max_capital_per_stock: 25,
    },
    prize_distribution: [
      { rank: 1, percentage: 35 },
      { rank: 2, percentage: 30 },
      { rank: 3, percentage: 20 },
      { rank: 4, percentage: 10 },
      { rank: 5, percentage: 5 },
    ],
    rules: "",
    category: "Equity",
    duration_tag: "Weekly",
    platform_stake: 15,
    info_box_text: "",
    first_prize_icon: "🏆",
    win_percentage_icon: "📈",
    max_entries_icon: "Ⓜ️",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stockUniverses, setStockUniverses] = useState<StockUniverse[]>([]);

  const isEditingDisabled =
    challenge && ["Live", "Completed", "Cancelled"].includes(challenge.status);
  const hasParticipants =
    challenge && (challenge.current_participants || 0) > 0;
  const isPrizeFieldsDisabled = hasParticipants || isEditingDisabled;
  const [loadingUniverses, setLoadingUniverses] = useState(true);
  const [cryptoUniverses, setCryptoUniverses] = useState<CryptoUniverse[]>([]);
  // Fetch stock universes on component mount
  // useEffect(() => {
  //   const fetchStockUniverses = async () => {
  //     try {
  //       const response = await stockService.getActiveStockUniverses();
  //       setStockUniverses(response.universes || []);
  //     } catch (error) {
  //       console.error("Error fetching stock universes:", error);
  //     }
  //   };

  //   fetchStockUniverses();
  // }, []);
  useEffect(() => {
    const fetchUniverses = async () => {
      setLoadingUniverses(true);
      try {
        if (isStockTradingMode) {
          const response = await stockService.getActiveStockUniverses();
          setStockUniverses(response.universes || []);
        } else {
          const response = await cryptoService.getActiveCryptoUniverses();
          setCryptoUniverses(response.universes || []);
        }
      } catch (error) {
        console.error("Error fetching universes:", error);
      } finally {
        setLoadingUniverses(false);
      }
    };

    fetchUniverses();
  }, [isStockTradingMode]);
  const formatDateTimeLocal = (dateString: any) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  useEffect(() => {
    if (challenge) {
      setFormData({
        name: challenge.name || "",
        description: challenge.description || "",
        type: challenge.type || "Weekly",
        entry_fee: challenge.entry_fee || 0,
        prize_pool: challenge.prize_pool || 0,
        max_participants: challenge.max_participants || 100,
        min_participants: challenge.min_participants || 2,
        gameplay_type: challenge.gameplay_type || "DYNAMIC",
        allow_overflow_entries: challenge.allow_overflow_entries || false,
        start_date: formatDateTimeLocal(challenge.start_date),
        end_date: formatDateTimeLocal(challenge.end_date),
        stock_universe_ids: challenge.stock_universe_ids || [],
        trading_hours_settings: {
          enabled: isStockTradingMode
            ? Boolean(challenge.trading_hours_settings?.enabled ?? true)
            : false,
          fixed_window_start_time:
            challenge.trading_hours_settings?.fixed_window_start_time ?? null,
          fixed_window_end_time:
            challenge.trading_hours_settings?.fixed_window_end_time ?? null,
          cancel_unexecuted_fixed_limits: Boolean(
            challenge.trading_hours_settings?.cancel_unexecuted_fixed_limits ??
              true
          ),
          restrict_to_market_hours: Boolean(
            challenge.trading_hours_settings?.restrict_to_market_hours ?? true
          ),
        },
        virtual_capital: challenge.virtual_capital || 1000000,
        brokerage_fee: challenge.brokerage_fee || 0.05,
        challenge_type: challenge.challenge_type || "Flexible",
        guaranteed_prize_pool: challenge.guaranteed_prize_pool || 0,
        allowed_order_types: challenge.allowed_order_types || {
          market_order: true,
          limit_orders: false,
          stop_loss_orders: false,
        },
        experience_level: challenge.experience_level,
        risk_appetite: challenge.risk_appetite,
        trading_restrictions: challenge.trading_restrictions || {
          allow_short_selling: false,
          intraday_only: false,
          max_open_positions: 10,
          max_capital_per_stock: 25,
        },
        prize_distribution: challenge.prize_distribution || [
          { rank: 1, percentage: 35 },
          { rank: 2, percentage: 30 },
          { rank: 3, percentage: 20 },
          { rank: 4, percentage: 10 },
          { rank: 5, percentage: 5 },
        ],
        rules: challenge.rules || "",
        category: challenge.category || "Equity",
        duration_tag: challenge.duration_tag || "Weekly",
        platform_stake: challenge.platform_stake || 15,
        info_box_text: challenge.info_box_text || "",
        first_prize_icon: challenge.first_prize_icon || "🏆",
        win_percentage_icon: challenge.win_percentage_icon || "📈",
        max_entries_icon: challenge.max_entries_icon || "Ⓜ️",
      });
    }
  }, [challenge]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Challenge name is required";
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Description is required";
    }

    if ((formData.entry_fee || 0) < 0) {
      newErrors.entry_fee = "Entry fee cannot be negative";
    }

    if ((formData.prize_pool || 0) < 0) {
      newErrors.prize_pool = "Prize pool cannot be negative";
    }

    if ((formData.max_participants || 0) < 0) {
      newErrors.max_participants = "Max participants cannot be negative";
    }

    if ((formData.min_participants || 0) < 2) {
      newErrors.min_participants = "Minimum participants must be at least 2";
    }

    if (
      formData.max_participants &&
      formData.min_participants &&
      formData.max_participants < formData.min_participants
    ) {
      newErrors.max_participants =
        "Max participants cannot be less than min participants";
    }

    if (
      formData.start_date &&
      formData.end_date &&
      new Date(formData.start_date) >= new Date(formData.end_date)
    ) {
      newErrors.end_date = "End date must be after start date";
    }

    if ((formData.stock_universe_ids || []).length === 0) {
      newErrors.stock_universe_ids =
        "At least one stock universe must be selected";
    }

    if ((formData.virtual_capital || 0) <= 0) {
      newErrors.virtual_capital = "Virtual capital must be greater than 0";
    }
    if (
      !formData.stock_universe_ids ||
      formData.stock_universe_ids.length === 0
    ) {
      newErrors.stock_universe_ids =
        "At least one stock universe must be selected";
    }
    if (
      (formData.brokerage_fee || 0) < 0 ||
      (formData.brokerage_fee || 0) > 1
    ) {
      newErrors.brokerage_fee = "Brokerage fee must be between 0 and 100%";
    }

    if (
      formData.challenge_type === "Guaranteed" &&
      (formData.guaranteed_prize_pool || 0) <= 0
    ) {
      newErrors.guaranteed_prize_pool =
        "Guaranteed prize pool must be greater than 0";
    }
    if (
      (formData.entry_fee || 0) > 0 &&
      (formData.prize_pool || 0) <= (formData.entry_fee || 0)
    ) {
      newErrors.prize_pool = "Prize pool must be greater than entry fee";
    }

    const totalPercentage =
      formData.prize_distribution?.reduce(
        (sum, prize) => sum + prize.percentage,
        0
      ) || 0;
    if (totalPercentage !== 100) {
      newErrors.prize_distribution =
        "Prize distribution percentages must total 100%";
    }
    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    const now = new Date();
    if (formData.start_date) {
      const start = new Date(formData.start_date);

      // check start date > now
      if (start < now) {
        newErrors.start_date = "Start date/time must be in the future";
      }

      if (formData.end_date) {
        const end = new Date(formData.end_date);

        // check end date > start date
        if (end <= start) {
          newErrors.end_date = "End date/time must be after start date/time";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if challenge status allows editing
    if (
      challenge &&
      ["Live", "Completed", "Cancelled"].includes(challenge.status)
    ) {
      toast.error(
        `Cannot edit challenges with status '${challenge.status}'. Only Draft and Upcoming challenges can be edited.`
      );
      return;
    }

    // Check if prize-related fields can be edited when participants exist
    // if (hasParticipants) {
    //   toast.error('⚠️ Cannot edit prize-related fields once users have participated in the challenge. Only non-prize fields can be modified.');
    //   return;
    // }

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleOrderTypeChange = (orderType: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      allowed_order_types: {
        ...prev.allowed_order_types!,
        [orderType]: checked,
      },
    }));
  };

  const handleTradingRestrictionChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      trading_restrictions: {
        ...prev.trading_restrictions!,
        [field]: value,
      },
    }));
  };

  const handlePrizeDistributionChange = (
    index: number,
    field: string,
    value: number
  ) => {
    setFormData((prev) => {
      const newPrizeDistribution = [...(prev.prize_distribution || [])];
      newPrizeDistribution[index] = {
        ...newPrizeDistribution[index],
        [field]: value,
      };
      return { ...prev, prize_distribution: newPrizeDistribution };
    });
  };

  const addPrizeDistribution = () => {
    setFormData((prev) => ({
      ...prev,
      prize_distribution: [
        ...(prev.prize_distribution || []),
        { rank: (prev.prize_distribution?.length || 0) + 1, percentage: 0 },
      ],
    }));
  };

  const removePrizeDistribution = (index: number) => {
    setFormData((prev) => {
      const newPrizeDistribution = (prev.prize_distribution || []).filter(
        (_, i) => i !== index
      );
      return { ...prev, prize_distribution: newPrizeDistribution };
    });
  };

  const getTotalPercentage = () => {
    return (
      formData.prize_distribution?.reduce(
        (sum, item) => sum + item.percentage,
        0
      ) || 0
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const handleTradingHoursChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      trading_hours_settings: {
        ...formData.trading_hours_settings,
        [field]: value,
      } as any,
    });
  };
  const HelpIcon = ({ text }: { text: string }) => (
    <div className="relative group">
      <FiHelpCircle className="inline ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
        {text}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-100 font-inter p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={onCancel}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              ← Back to Challenge List
            </button>
            <h1 className="text-3xl font-bold text-blue-800">
              {challenge ? "Edit Challenge" : "Create New Challenge"}
            </h1>
          </div>
        </div>

        {/* Form */}
        <div
          className={`bg-white rounded-lg shadow-md p-6 ${
            isEditingDisabled ? "opacity-75" : ""
          }`}
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Status Warning */}
            {challenge &&
              ["Live", "Completed", "Cancelled"].includes(challenge.status) && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Editing Restricted
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          This challenge has a status of{" "}
                          <strong>{challenge.status}</strong>. Only Draft and
                          Upcoming challenges can be edited.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Participants Warning */}
            {hasParticipants && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Prize Fields Locked
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        This challenge has{" "}
                        <strong>
                          {challenge.current_participants} participant(s)
                        </strong>
                        . Challenge name and prize-related fields cannot be
                        modified once users have participated.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {Object.values(errors).some((e) => e && e.trim() !== "") && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <ul className="text-red-600 text-sm">
                  {Object.values(errors)
                    .filter((e) => e && e.trim() !== "")
                    .map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                </ul>
              </div>
            )}

            {/* Section 1: Basic Details & Categorization */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 1: Basic Details & Categorization
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } ${
                      isPrizeFieldsDisabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                    placeholder="Enter challenge name"
                    autoComplete="off"
                    disabled={isPrizeFieldsDisabled}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.description ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Describe the challenge, objectives, and key features"
                    autoComplete="off"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.description}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="start_date"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Start Date *
                    </label>
                    <input
                      type="datetime-local"
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.start_date ? "border-red-300" : "border-gray-300"
                      }`}
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.start_date}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="end_date"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      End Date *
                    </label>
                    <input
                      type="datetime-local"
                      id="end_date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.end_date ? "border-red-300" : "border-gray-300"
                      }`}
                    />
                    {errors.end_date && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.end_date}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="duration_tag"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Duration Tag *
                    </label>
                    <select
                      id="duration_tag"
                      name="duration_tag"
                      value={formData.duration_tag}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.duration_tag
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                    {errors.duration_tag && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.duration_tag}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="max_participants"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Max Participants
                    </label>
                    <input
                      type="number"
                      id="max_participants"
                      name="max_participants"
                      value={formData.max_participants || 0}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.max_participants
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      min="1"
                    />
                    {errors.max_participants && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.max_participants}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="min_participants"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Min Participants *
                    </label>
                    <input
                      type="number"
                      id="min_participants"
                      name="min_participants"
                      value={formData.min_participants || 2}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.min_participants
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      min="2"
                    />
                    {errors.min_participants && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.min_participants}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Minimum participants required for challenge to start
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="gameplay_type"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Gameplay Type *
                      <span
                        title="Dynamic: Users can trade during the challenge. Fixed: Users set portfolio at start, no trading during challenge."
                        className="ml-1 cursor-help"
                      >
                        🛈
                      </span>
                    </label>
                    <select
                      id="gameplay_type"
                      name="gameplay_type"
                      value={formData.gameplay_type}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.gameplay_type
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="DYNAMIC">Dynamic Portfolio</option>
                      <option value="FIXED">Fixed Portfolio</option>
                    </select>
                    {errors.gameplay_type && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.gameplay_type}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      {formData.gameplay_type === "DYNAMIC"
                        ? "Users can actively trade during the challenge."
                        : "Users set portfolio at start, no trading during challenge."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Financial Details */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 2: Financial Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="entry_fee"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Entry Fee (₹)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      id="entry_fee"
                      name="entry_fee"
                      value={formData.entry_fee}
                      onChange={handleInputChange}
                      className={`w-full pl-7 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.entry_fee ? "border-red-300" : "border-gray-300"
                      } ${
                        isPrizeFieldsDisabled
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }`}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      disabled={isPrizeFieldsDisabled}
                    />
                  </div>
                  {errors.entry_fee && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.entry_fee}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Set to 0 for free entry
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="prize_pool"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Prize Pool (₹)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">₹</span>
                    </div>
                    <input
                      type="number"
                      id="prize_pool"
                      name="prize_pool"
                      value={formData.prize_pool}
                      onChange={handleInputChange}
                      className={`w-full pl-7 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.prize_pool ? "border-red-300" : "border-gray-300"
                      } ${
                        isPrizeFieldsDisabled
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }`}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      disabled={isPrizeFieldsDisabled}
                    />
                  </div>
                  {errors.prize_pool && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.prize_pool}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Total prize money to be distributed
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Financial Summary
                    </h3>
                    {formData.entry_fee > 0 && (
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Entry Fee: {formatCurrency(formData.entry_fee)}</p>
                        {(formData.max_participants || 0) > 0 && (
                          <p>
                            Max Revenue:{" "}
                            {formatCurrency(
                              (formData.entry_fee || 0) *
                                (formData.max_participants || 0)
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Trading Rules */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 3: Trading Rules
                {formData.gameplay_type === "FIXED" && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (Not applicable for Fixed Portfolio challenges)
                  </span>
                )}
              </h2>
              {isPrizeFieldsDisabled && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm text-gray-600">
                    Trading rules cannot be modified when participants exist or
                    challenge is in restricted status.
                  </p>
                </div>
              )}
              {formData.gameplay_type === "FIXED" && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Fixed Portfolio Challenge
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          In Fixed Portfolio challenges, users set their stock
                          portfolio at the start and cannot trade during the
                          challenge. Trading rules are not applicable.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                  formData.gameplay_type === "FIXED" || isPrizeFieldsDisabled
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
              >
                <div>
                  <label
                    htmlFor="max_open_positions"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Max Open Positions
                  </label>
                  <input
                    type="number"
                    id="max_open_positions"
                    name="max_open_positions"
                    value={
                      formData.trading_restrictions?.max_open_positions || 10
                    }
                    onChange={(e) =>
                      handleTradingRestrictionChange(
                        "max_open_positions",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="1"
                    disabled={
                      formData.gameplay_type === "FIXED" ||
                      isPrizeFieldsDisabled
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="max_capital_per_stock"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Max Capital per Stock (%)
                  </label>
                  <input
                    type="number"
                    id="max_capital_per_stock"
                    name="max_capital_per_stock"
                    value={
                      formData.trading_restrictions?.max_capital_per_stock || 25
                    }
                    onChange={(e) =>
                      handleTradingRestrictionChange(
                        "max_capital_per_stock",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="1"
                    max="100"
                    disabled={
                      formData.gameplay_type === "FIXED" ||
                      isPrizeFieldsDisabled
                    }
                  />
                </div>
              </div>

              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                  formData.gameplay_type === "FIXED" || isPrizeFieldsDisabled
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
              >
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Allowed Order Types
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="market_order"
                        name="market_order"
                        checked={
                          formData.allowed_order_types?.market_order || false
                        }
                        disabled={isPrizeFieldsDisabled}
                        className="mr-2"
                      />
                      <label
                        htmlFor="market_order"
                        className="text-sm text-gray-700"
                      >
                        Market Orders (Always Enabled)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="limit_orders"
                        name="limit_orders"
                        checked={
                          formData.allowed_order_types?.limit_orders || false
                        }
                        onChange={(e) =>
                          handleOrderTypeChange(
                            "limit_orders",
                            e.target.checked
                          )
                        }
                        className="mr-2"
                        disabled={
                          formData.gameplay_type === "FIXED" ||
                          isPrizeFieldsDisabled
                        }
                      />
                      <label
                        htmlFor="limit_orders"
                        className="text-sm text-gray-700"
                      >
                        Limit Orders
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="stop_loss_orders"
                        name="stop_loss_orders"
                        checked={
                          formData.allowed_order_types?.stop_loss_orders ||
                          false
                        }
                        onChange={(e) =>
                          handleOrderTypeChange(
                            "stop_loss_orders",
                            e.target.checked
                          )
                        }
                        className="mr-2"
                        disabled={
                          formData.gameplay_type === "FIXED" ||
                          isPrizeFieldsDisabled
                        }
                      />
                      <label
                        htmlFor="stop_loss_orders"
                        className="text-sm text-gray-700"
                      >
                        Stop-Loss Orders
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Trading Restrictions
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="allow_short_selling"
                        name="allow_short_selling"
                        checked={
                          formData.trading_restrictions?.allow_short_selling ||
                          false
                        }
                        onChange={(e) =>
                          handleTradingRestrictionChange(
                            "allow_short_selling",
                            e.target.checked
                          )
                        }
                        className="mr-2"
                        disabled={
                          formData.gameplay_type === "FIXED" ||
                          isPrizeFieldsDisabled
                        }
                      />
                      <label
                        htmlFor="allow_short_selling"
                        className="text-sm text-gray-700"
                      >
                        Allow Short Selling
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="intraday_only"
                        name="intraday_only"
                        checked={
                          formData.trading_restrictions?.intraday_only || false
                        }
                        onChange={(e) =>
                          handleTradingRestrictionChange(
                            "intraday_only",
                            e.target.checked
                          )
                        }
                        disabled={
                          formData.gameplay_type === "FIXED" ||
                          isPrizeFieldsDisabled
                        }
                        className="mr-2"
                      />
                      <label
                        htmlFor="intraday_only"
                        className="text-sm text-gray-700"
                      >
                        Intraday Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Prize Distribution */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 4: Prize Distribution
              </h2>
              {isPrizeFieldsDisabled && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm text-gray-600">
                    Prize distribution cannot be modified when participants
                    exist or challenge is in restricted status.
                  </p>
                </div>
              )}
              <div className="space-y-4">
                {(formData.prize_distribution || []).map((prize, index) => {
                  const netPrizePool =
                    (formData.prize_pool || 0) *
                    (1 - (formData.platform_stake || 0) / 100);

                  // 2️⃣ Each tier’s share
                  const prizeAmount = (prize.percentage / 100) * netPrizePool;
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            index === 0
                              ? "bg-yellow-100"
                              : index === 1
                              ? "bg-gray-100"
                              : index === 2
                              ? "bg-orange-100"
                              : "bg-blue-100"
                          }`}
                        >
                          <span
                            className={`text-lg font-bold ${
                              index === 0
                                ? "text-yellow-600"
                                : index === 1
                                ? "text-gray-600"
                                : index === 2
                                ? "text-orange-600"
                                : "text-blue-600"
                            }`}
                          >
                            {prize.rank}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {index === 0
                              ? "🥇 1st Place"
                              : index === 1
                              ? "🥈 2nd Place"
                              : index === 2
                              ? "🥉 3rd Place"
                              : `${prize.rank}th Place`}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {prize.percentage}% of prize pool (after{" "}
                            {formData.platform_stake}% fee)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(prizeAmount)}
                        </p>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={addPrizeDistribution}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  + Add Prize Tier
                </button>

                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-600">
                    Total:{" "}
                    <span className="font-medium">{getTotalPercentage()}%</span>
                    {getTotalPercentage() !== 100 && (
                      <span className="text-red-600 ml-2">
                        (Must equal 100%)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5: Stock Universe and Virtual Capital */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 5: Stock Universe and Virtual Capital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* <div>
                  <label
                    htmlFor="stock_universe_ids"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Stock Universe *
                  </label>
                  <select
                    id="stock_universe_ids"
                    name="stock_universe_ids"
                    value={formData.stock_universe_ids}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.stock_universe_ids
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    multiple
                  >
                    {stockUniverses.map((universe) => (
                      <option key={universe.id} value={universe.id}>
                        {universe.name}
                      </option>
                    ))}
                  </select>
                  {errors.stock_universe_ids && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.stock_universe_ids}
                    </p>
                  )}
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isStockTradingMode
                      ? "Stock Universe *"
                      : "Crypto Universe *"}{" "}
                    <HelpIcon
                      text={`Define which ${
                        isStockTradingMode ? "stocks" : "cryptos"
                      } are tradable within this challenge`}
                    />
                  </label>

                  {loadingUniverses ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      Loading {isStockTradingMode ? "stock" : "crypto"}{" "}
                      universes...
                    </div>
                  ) : (
                    <div className="w-full border border-gray-300 rounded-md p-3 max-h-32 overflow-y-auto">
                      {(isStockTradingMode ? stockUniverses : cryptoUniverses)
                        .length === 0 ? (
                        <p className="text-gray-500 text-sm">
                          No {isStockTradingMode ? "stock" : "crypto"} universes
                          available. Please create some first.
                        </p>
                      ) : (
                        (isStockTradingMode
                          ? stockUniverses
                          : cryptoUniverses
                        ).map((universe) => (
                          <label
                            key={universe.id}
                            className="flex items-center space-x-2 py-1"
                          >
                            <input
                              type="checkbox"
                              value={universe.id}
                              checked={formData.stock_universe_ids?.includes(
                                universe.id
                              )}
                              onChange={(e) => {
                                const selectedIds =
                                  formData.stock_universe_ids || [];

                                if (e.target.checked) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    stock_universe_ids: [
                                      ...selectedIds,
                                      universe.id,
                                    ],
                                  }));
                                } else {
                                  setFormData((prev) => ({
                                    ...prev,
                                    stock_universe_ids: selectedIds.filter(
                                      (id) => id !== universe.id
                                    ),
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-900">
                              {universe.name}
                            </span>
                            {universe.description && (
                              <span className="text-xs text-gray-500">
                                ({universe.description})
                              </span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="virtual_capital"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Virtual Capital *
                  </label>
                  <input
                    type="number"
                    id="virtual_capital"
                    name="virtual_capital"
                    value={formData.virtual_capital}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.virtual_capital
                        ? "border-red-300"
                        : "border-gray-300"
                    } ${
                      isPrizeFieldsDisabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                    min="0"
                    placeholder="1,000,000"
                    disabled={isPrizeFieldsDisabled}
                  />
                  {errors.virtual_capital && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.virtual_capital}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 6: Brokerage Fee */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 6: Brokerage Fee
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="brokerage_fee"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Brokerage Fee *
                  </label>
                  <input
                    type="number"
                    id="brokerage_fee"
                    name="brokerage_fee"
                    value={formData.brokerage_fee}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.brokerage_fee
                        ? "border-red-300"
                        : "border-gray-300"
                    } ${
                      isPrizeFieldsDisabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                    min="0"
                    max="1"
                    step="0.01"
                    placeholder="0.05"
                    disabled={isPrizeFieldsDisabled}
                  />
                  {errors.brokerage_fee && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.brokerage_fee}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 7: Challenge Type and Guaranteed Prize Pool */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 7: Challenge Type and Guaranteed Prize Pool
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="challenge_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Type *
                  </label>
                  <select
                    id="challenge_type"
                    name="challenge_type"
                    value={formData.challenge_type}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.challenge_type
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="Flexible">Flexible</option>
                    <option value="Guaranteed">Guaranteed</option>
                  </select>
                  {errors.challenge_type && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.challenge_type}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="guaranteed_prize_pool"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Guaranteed Prize Pool
                  </label>
                  <input
                    type="number"
                    id="guaranteed_prize_pool"
                    name="guaranteed_prize_pool"
                    value={formData.guaranteed_prize_pool}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.guaranteed_prize_pool
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    min="0"
                    placeholder="0"
                  />
                  {errors.guaranteed_prize_pool && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.guaranteed_prize_pool}
                    </p>
                  )}
                </div>
              </div>

              {/* Allow Overflow Entries - Only for Flexible challenges */}
              {formData.challenge_type === "Flexible" && (
                <div className="mt-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allow_overflow_entries"
                      name="allow_overflow_entries"
                      checked={formData.allow_overflow_entries}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="allow_overflow_entries"
                      className="ml-2 block text-sm font-medium text-gray-700"
                    >
                      Allow Overflow Entries
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    When enabled, participants can join even after
                    max_participants is reached, causing the prize pool to grow
                    dynamically.
                  </p>
                </div>
              )}
            </div>

            {/* Section 8: Platform Stake */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 8: Platform Stake
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="platform_stake"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Platform Stake (%)
                  </label>
                  <input
                    type="number"
                    id="platform_stake"
                    name="platform_stake"
                    value={formData.platform_stake}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.platform_stake
                        ? "border-red-300"
                        : "border-gray-300"
                    } ${
                      isPrizeFieldsDisabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="15"
                    disabled={isPrizeFieldsDisabled}
                  />
                  {errors.platform_stake && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.platform_stake}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 9: Trading Hours Settings */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 9: Trading Hours Settings
              </h2>

              {/* Master Toggle */}
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trading_hours_enabled"
                    checked={formData.trading_hours_settings?.enabled || false}
                    onChange={(e) =>
                      handleTradingHoursChange("enabled", e.target.checked)
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="trading_hours_enabled"
                    className="ml-2 block text-sm font-medium text-gray-700"
                  >
                    Enable Trading Hours Restrictions
                  </label>
                  <HelpIcon text="Master toggle to enforce specific trading hours for this challenge. If disabled, trading is 24/7 during the Live phase." />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  When enabled, trading will be restricted to specific hours.
                  When disabled, trading is allowed 24/7.
                </p>
              </div>

              {formData.trading_hours_settings?.enabled && (
                <div className="space-y-6">
                  {/* Fixed Window Settings */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-4">
                      Fixed Trading Window
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="fixed_window_start_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Start Time (HH:mm)
                        </label>
                        <input
                          type="time"
                          id="fixed_window_start_time"
                          value={
                            formData.trading_hours_settings
                              ?.fixed_window_start_time || ""
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "fixed_window_start_time",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="09:00"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          e.g., 09:00 for 9:00 AM
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="fixed_window_end_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          End Time (HH:mm)
                        </label>
                        <input
                          type="time"
                          id="fixed_window_end_time"
                          value={
                            formData.trading_hours_settings
                              ?.fixed_window_end_time || ""
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "fixed_window_end_time",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="15:30"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          e.g., 15:30 for 3:30 PM
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Challenge Type Specific Settings */}
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <h3 className="text-lg font-medium text-green-900 mb-4">
                      Challenge Type Specific Settings
                    </h3>

                    {formData.challenge_type === "Guaranteed" && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="cancel_unexecuted_fixed_limits"
                          checked={
                            formData.trading_hours_settings
                              ?.cancel_unexecuted_fixed_limits || false
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "cancel_unexecuted_fixed_limits",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="cancel_unexecuted_fixed_limits"
                          className="ml-2 block text-sm font-medium text-gray-700"
                        >
                          Cancel Unexecuted Fixed Limits
                        </label>
                        <HelpIcon text="When enabled, any unexecuted limit orders will be cancelled when the trading window closes." />
                      </div>
                    )}

                    {formData.challenge_type === "Flexible" && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="restrict_to_market_hours"
                          checked={
                            formData.trading_hours_settings
                              ?.restrict_to_market_hours || false
                          }
                          onChange={(e) =>
                            handleTradingHoursChange(
                              "restrict_to_market_hours",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="restrict_to_market_hours"
                          className="ml-2 block text-sm font-medium text-gray-700"
                        >
                          Restrict to Market Hours (9:15 AM - 3:30 PM)
                        </label>
                        <HelpIcon text="When enabled, trading will be restricted to standard market hours (9:15 AM to 3:30 PM IST)." />
                      </div>
                    )}
                  </div>

                  {/* Information Box */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Trading Hours Information
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <ul className="list-disc list-inside space-y-1">
                            <li>
                              <strong>Fixed Challenges:</strong> Initial
                              portfolio can be defined anytime on Start Date
                            </li>
                            <li>
                              <strong>Dynamic Challenges:</strong> Trading is
                              restricted to specified hours during Live phase
                            </li>
                            <li>
                              <strong>Market Hours:</strong> Standard NSE
                              trading hours are 9:15 AM to 3:30 PM IST
                            </li>
                            <li>
                              <strong>Limit Orders:</strong> Can be placed
                              outside trading hours but executed only during
                              trading hours
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 10: Info Box Text */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 10: Info Box Text
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="info_box_text"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Info Box Text
                  </label>
                  <textarea
                    id="info_box_text"
                    name="info_box_text"
                    rows={4}
                    value={formData.info_box_text}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.info_box_text
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter info box text"
                  />
                  {errors.info_box_text && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.info_box_text}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 11: First Prize Icon */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 11: First Prize Icon
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="first_prize_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    First Prize Icon *
                  </label>
                  <input
                    type="text"
                    id="first_prize_icon"
                    name="first_prize_icon"
                    value={formData.first_prize_icon}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.first_prize_icon
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter first prize icon"
                  />
                  {errors.first_prize_icon && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.first_prize_icon}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 12: Win Percentage Icon */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 12: Win Percentage Icon
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="win_percentage_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Win Percentage Icon *
                  </label>
                  <input
                    type="text"
                    id="win_percentage_icon"
                    name="win_percentage_icon"
                    value={formData.win_percentage_icon}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.win_percentage_icon
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter win percentage icon"
                  />
                  {errors.win_percentage_icon && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.win_percentage_icon}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 13: Max Entries Icon */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 13: Max Entries Icon
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="max_entries_icon"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Max Entries Icon *
                  </label>
                  <input
                    type="text"
                    id="max_entries_icon"
                    name="max_entries_icon"
                    value={formData.max_entries_icon}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.max_entries_icon
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter max entries icon"
                  />
                  {errors.max_entries_icon && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.max_entries_icon}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 14: Rules */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 14: Rules
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label
                    htmlFor="rules"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Challenge Rules *
                  </label>
                  <textarea
                    id="rules"
                    name="rules"
                    rows={6}
                    value={formData.rules}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.rules ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Enter detailed challenge rules and guidelines..."
                  />
                  {errors.rules && (
                    <p className="mt-1 text-sm text-red-600">{errors.rules}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 15: Category */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Section 15: Category
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.category ? "border-red-300" : "border-gray-300"
                    }`}
                  >
                    <option value="Equity">Equity</option>
                    <option value="Futures">Futures</option>
                    <option value="Options">Options</option>
                    <option value="Currency">Currency</option>
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.category}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation and Submit */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <div className="flex space-x-3">
                {/* Add navigation buttons here */}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || isEditingDisabled}
                  className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {challenge ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditingDisabled ? (
                    <>
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      Editing Not Allowed
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {challenge ? "Update Challenge" : "Create Challenge"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
